<template>
  <div v-if="newTaskCard" class="w-100 d-flex flex-column">
    <proa-heading>{{ $t('pages.project.taskCards.new.heading') }}</proa-heading>
    <proa-card
      :class="['new-task-card-card', {
        'disabled': createTaskCardRequesting
      }]"
    >
      <table class="table-attributes">
        <tbody>
          <tr>
            <th>{{ $t('models.taskCard.attributes.title') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editTitleModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span :class="['attribute', { 'empty': !newTaskCard.title }]">{{ newTaskCard.title || $t('models.taskCard.placeHolders.title') }}</span>
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.taskCard.attributes.taskListId') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editTaskListIdModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span :class="['attribute', { 'empty': !newTaskCard.taskListId }]">{{ selectedTaskListTitle(newTaskCard.taskListId) || $t('models.taskCard.placeHolders.taskListId') }}</span>
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.taskCard.period') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editPeriodModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span :class="['attribute', { 'empty': !newTaskCard.startAt && !newTaskCard.endAt }]">
                  <template v-if="!newTaskCard.startAt && !newTaskCard.endAt">
                    {{ $t('models.taskCard.placeHolders.period') }}
                  </template>
                  <template v-else>
                    {{ newTaskCard.startAt | moment('YYYY年MM月DD日（ddd）') }} 〜 {{ newTaskCard.endAt | moment('YYYY年MM月DD日（ddd）') }}
                  </template>
                </span>
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.taskCard.attributes.progress') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editProgressModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span class="attribute">
                  {{ newTaskCard.progress }}%
                </span>
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.taskCard.attributes.projectLabelIds') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editProjectLabelIdsModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span :class="['attribute', { 'empty': !newTaskCard.projectLabelIds.length }]">
                  <div v-if="newTaskCard.projectLabelIds.length" class="project-labels-wrapper">
                    <proa-project-label
                      v-for="newProjectLabel in newTaskCard.projectLabelIds"
                      :key="newProjectLabel.id"
                      :title="selectedProjectLabel(newProjectLabel).title"
                      :color="selectedProjectLabel(newProjectLabel).color"
                    />
                  </div>
                  <template v-else>
                    {{ $t('models.taskCard.placeHolders.projectLabelIds') }}
                  </template>
                </span>
              </span>
            </td>
          </tr>
          <tr>
            <th valign="top">{{ $t('models.taskCard.attributes.description') }}</th>
            <td>
              <span class="attribute-wrapper" @click="editDescriptionModal = true">
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span
                  v-html="newTaskCard.description ? $options.filters.autoLink(newTaskCard.description) : $t('models.taskCard.placeHolders.description')"
                  :class="['attribute text-pre-wrap', { 'empty': !newTaskCard.description }]"
                />
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.taskCard.users') }}（{{ newTaskCard.userIds.length }}{{ $t('common.person') }}）</th>
            <td>
              <proa-button
                @click.native="addTaskCardMembersModal = true"
                class="default-button-width mr-2"
              >
                {{ $t('common.addMember') }}
              </proa-button>
              <proa-button
                :disabled="newTaskCard.userIds.length == 0"
                @click.native="deleteTaskCardMembersModal = true"
                class="default-button-width"
              >
                {{ $t('common.deleteMember') }}
              </proa-button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="members">
        <div class="members-scroll-wrapper">
          <div v-for="user in addedUsers" :key="user.id" class="member">
            <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
            <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
          </div>
        </div>
      </div>
      <div class="new-task-card-card-footer">
        <proa-button
           @click.native="$router.push({ name: 'showProject', params: { id: projectId } })"
           class="mr-2 default-button-width"
         >
           {{ $t('common.cancel') }}
         </proa-button>
         <proa-button
           :disabled="!validated"
           :loading="createTaskCardRequesting"
           variant="primary"
           class="default-button-width"
           @click.native="onClickCreateTaskCard"
         >
           {{ $t('common.create') }}
         </proa-button>
      </div>
    </proa-card>
    <edit-title-modal
      :modal="editTitleModal"
      :title="newTaskCard.title"
      @close="editTitleModal = false"
      @onUpdateTitle="onUpdateTitle"
    />
    <edit-task-list-id-modal
      :modal="editTaskListIdModal"
      :taskListId="newTaskCard.taskListId"
      :taskLists="taskLists"
      @close="editTaskListIdModal = false"
      @onUpdateTaskListId="onUpdateTaskListId"
    />
    <edit-period-modal
      :modal="editPeriodModal"
      :startAt="newTaskCard.startAt"
      :endAt="newTaskCard.endAt"
      @close="editPeriodModal = false"
      @onUpdatePeriod="onUpdatePeriod"
    />
    <edit-progress-modal
      :modal="editProgressModal"
      :progress="newTaskCard.progress"
      @close="editProgressModal = false"
      @onUpdateProgress="onUpdateProgress"
    />
    <edit-project-label-check-box-modal
      :modal="editProjectLabelIdsModal"
      :projectLabelIds="newTaskCard.projectLabelIds"
      :projectLabels="this.projectLabels"
      @close="editProjectLabelIdsModal = false"
      @onUpdateProjectLabelIds="onUpdateProjectLabelIds"
    />
    <edit-description-modal
      :modal="editDescriptionModal"
      :description="newTaskCard.description"
      @close="editDescriptionModal = false"
      @onUpdateDescription="onUpdateDescription"
    />
    <select-member-modal
      :modal="addTaskCardMembersModal"
      :designated="true"
      :userIds="newTaskCard.userIds"
      :designatedUserIds="project.userIds"
      type="add"
      @close="addTaskCardMembersModal = false"
      @confirm="addUserIds"
    />
    <select-member-modal
      :modal="deleteTaskCardMembersModal"
      :designated="true"
      :userIds="newTaskCard.userIds"
      type="delete"
      @close="deleteTaskCardMembersModal = false"
      @confirm="deleteUserIds"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import Project from '@/store/models/Project'
import TaskList from '@/store/models/TaskList'
import TaskCard from '@/store/models/TaskCard'
import User from '@/store/models/User'

import EditTitleModal from '@/components/molecules/project/taskCards/EditTitleModal'
import EditTaskListIdModal from '@/components/molecules/project/taskCards/EditTaskListIdModal'
import EditPeriodModal from '@/components/molecules/project/taskCards/EditPeriodModal'
import EditProgressModal from '@/components/molecules/project/taskCards/EditProgressModal'
import EditProjectLabelCheckBoxModal from '@/components/molecules/project/taskCards/EditProjectLabelCheckBoxModal'
import EditDescriptionModal from '@/components/molecules/project/taskCards/EditDescriptionModal'
import SelectMemberModal from '@/components/organisms/SelectMemberModal'

export default {
  name: 'NewProjectTaskCards',
  components: {
    EditTitleModal,
    EditTaskListIdModal,
    EditPeriodModal,
    EditProgressModal,
    EditProjectLabelCheckBoxModal,
    EditDescriptionModal,
    SelectMemberModal
  },
  computed: {
    projectId: function() { return this.$route.params.id },
    project: function() { return Project.find(this.projectId) },
    projectLabels: function() { return this.project.projectLabels },
    taskListId: function() { return this.$route.params.taskListId },
    taskLists: function() {
      return TaskList.query().where((taskList) => {
        return taskList.projectId == this.projectId && taskList.listType != 'archive'
      }).orderBy('listIndex', 'asc').get()
    },
    selectedTaskListTitle: function() {
      return function(taskListId) {
        return TaskList.find(taskListId)?.title || ''
      }
    },
    selectedProjectLabel: function() {
      return function(projectLabelId) {
        return this.projectLabels.filter(projectLabel => projectLabel.id == projectLabelId)[0]
      }
    },
    addedUsers: function() {
      return User.findIn(this.newTaskCard.userIds)
    },
    validated: function() {
      return this.newTaskCard.title &&
             this.newTaskCard.taskListId
    },
    ...mapGetters({
      httpError: 'httpError'
    })
  },
  data() {
    return {
      newTaskCard: null,
      editTitleModal: false,
      editTaskListIdModal: false,
      editPeriodModal: false,
      editProgressModal: false,
      editProjectLabelIdsModal: false,
      editDescriptionModal: false,
      addTaskCardMembersModal: false,
      deleteTaskCardMembersModal: false,
      createTaskCardRequesting: false
    }
  },
  methods: {
    onUpdateTitle: function(title) {
      this.newTaskCard.title = title
      this.editTitleModal = false
    },
    onUpdateTaskListId: function(taskListId) {
      this.newTaskCard.taskListId = taskListId
      this.editTaskListIdModal = false
    },
    onUpdatePeriod: function(startAt, endAt) {
      this.newTaskCard.startAt = startAt
      this.newTaskCard.endAt = endAt
      this.editPeriodModal = false
    },
    onUpdateProgress: function(progress) {
      this.newTaskCard.progress = progress
      this.editProgressModal = false
    },
    onUpdateProjectLabelIds: function(projectLabelIds) {
      if (projectLabelIds[0] == null) {
        this.newTaskCard.projectLabelIds = []
      } else {
        this.newTaskCard.projectLabelIds = projectLabelIds
      }
      this.editProjectLabelIdsModal = false
    },
    onUpdateDescription: function(description) {
      this.newTaskCard.description = description
      this.editDescriptionModal = false
    },
    addUserIds: function({ userIds }) {
      this.addTaskCardMembersModal = false
      this.newTaskCard.userIds = this.newTaskCard.userIds.concat(userIds)
    },
    deleteUserIds: function({ userIds }) {
      this.deleteTaskCardMembersModal = false
      this.newTaskCard.userIds = this.newTaskCard.userIds.filter(userId => !userIds.includes(userId))
    },
    async onClickCreateTaskCard() {
      this.createTaskCardRequesting = true
      await this.apiPostTaskCard({
        projectId: this.projectId,
        attributes: this.newTaskCard
      })
      this.createTaskCardRequesting = false

      if (!this.httpError) {
        this.$router.push({ name: 'showProject', params: { id: this.projectId } })
      }
    },
    ...mapActions({
      apiGetProject: 'entities/projects/show',
      apiGetTaskLists: 'entities/taskLists/index',
      apiPostTaskCard: 'entities/taskCards/create'
    })
  },
  async created() {
    if (!this.project) {
      await this.apiGetProject(this.projectId)
      if (!this.project) {
        this.$router.push({ name: 'project' })
      }
    }
    await this.apiGetTaskLists(this.projectId)
    this.newTaskCard = TaskCard.skeleton()
    if (this.taskListId) this.newTaskCard.taskListId = this.taskListId
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.new-task-card-card {
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  flex-grow: 1;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .table-attributes {
    width: 100%;

    tr:not(:last-child) {
      border-bottom: 1px solid $dark-border-color;
    }

    th {
      width: 180px;
      white-space: nowrap;
      padding: 20px 0 20px 20px;
      font-weight: 600;
    }

    tr:last-child th {
      padding: 40px 0 40px 20px;
    }

    td {
      padding: 20px 0;
      img {
        width: 20px;
        height: 20px;
      }

      .attribute-wrapper {
        display: flex;
        cursor: pointer;

        &.invalid .attribute {
          color: $error-color;
        }
      }

      .attribute {
        margin-left: 10px;

        &.empty {
          color: $link-color;
        }

        .project-labels-wrapper {
          height: 100%;
          display: flex;
          flex-wrap: wrap;
        }
      }
    }
  }

  .members {
    flex-grow: 1;
    position: relative;

    .members-scroll-wrapper {
      position: absolute;
      height: 100%;
      width: 100%;
      padding-left: 20px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;

      .member {
        margin-bottom: 10px;

        &:last-child::after {
          content: '';
          display: block;
          height: 20px;
        }
      }
    }
  }

  .new-task-card-card-footer {
    display: flex;
    justify-content: flex-end;
    padding: 20px;
    border-top: 1px solid $dark-border-color;
  }
}
</style>
